import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';

import Input from '../common/InputWithLabel';
import PasswordGroup from '../common/PasswordGroup';
import Select2 from '../common/Select2';
import useInput from '../../hooks/useInput';
import CheckBox from '../common/Checkbox';
import PrimaryButton from '../common/PrimaryButton';
import findErrorField from '../../helpers/serverError';
import UserValidator from '../../helpers/validators/add-edit-user.validator';
import PasswordValidator from '../../helpers/validators/password.validator';
import Format from '../../helpers/format';
import Normalizer from '../../helpers/normalizers';
import useSelect2 from '../../hooks/useSelect2';

const UserForm = ({
  style,
  userState,
  userState: { isEditUser, isNewUser, user: editUser, securityLevels, hubs },
  history,
}) => {
  const firstName = useInput((editUser && editUser.FirstName) || '');
  const lastName = useInput((editUser && editUser.LastName) || '');
  const email = useInput((editUser && editUser.Email) || '');
  const officePhone = useInput((editUser && editUser.OfficePhone) || '');
  const officePhoneExtension = useInput(
    (editUser && editUser.OfficeExtension) || '',
  );
  const cellPhone = useInput((editUser && editUser.CellPhone) || '');
  const password = useInput((editUser && editUser.Password) || '');
  const passwordConfirm = useInput(
    (editUser && editUser.PasswordConfirm) || '',
  );
  const securityLevel = useSelect2((editUser && editUser.SecurityLevel) || '');
  const agencyId = useSelect2((editUser && editUser.AgencyId) || '');

  const [isLocked, setIsLocked] = useState(
    (editUser && editUser.IsLocked) || false,
  );
  const [isChangePassword, setIsChangePassword] = useState(false);

  const prevEmail = editUser && editUser.PrevEmail;
  const userId = editUser && editUser.Id;

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    officePhone: '',
    officeExtension: '',
    cellPhone: '',
    password: '',
    passwordConfirm: '',
    securityLevel: '',
    agencyId: '',
  });

  const [, setServerError] = useState('');

  useEffect(() => {
    if (isEditUser) {
      const existedSecurityLevel =
        securityLevels &&
        securityLevels.find(
          item => item.label.toString() === editUser.SecurityLevel.toString(),
        );
      if (existedSecurityLevel) {
        securityLevel.setValue({
          data: null,
          label: existedSecurityLevel.label,
          value: existedSecurityLevel.value,
        });
      }
      const existedAgencyName =
        hubs &&
        hubs.find(
          item => item.value.toString() === editUser.AgencyId.toString(),
        );
      if (existedAgencyName) {
        agencyId.setValue({
          data: null,
          label: existedAgencyName.label,
          value: existedAgencyName.value,
        });
      }
    }
    if (isNewUser) {
      const securityLevelInitialvalue = securityLevels && securityLevels[1];
      const agencyNameInitialvalue = hubs && hubs[0];

      securityLevel.setValue({
        data: null,
        label: securityLevelInitialvalue.label,
        value: securityLevelInitialvalue.value,
      });
      agencyId.setValue({
        data: null,
        label: agencyNameInitialvalue.label,
        value: agencyNameInitialvalue.value,
      });
    }
  }, []);

  const requestCallBack = requestConfig => {
    const handleSucces = ({ status }) => {
      if (status === 200) {
        // return to manage users page
        if (isEditUser)
          history.push({
            pathname: '/users',
            state: { ...userState, isEditUser: false },
          });
        else {
          history.push({
            pathname: '/users',
            state: { ...userState, isNewUser: false },
          });
        }
      }
    };

    const handleError = error => {
      if (
        error.response &&
        error.response.data &&
        error.response.data.Message
      ) {
        setServerError(error.response.data.Message);
      }

      if (
        error.response &&
        error.response.data &&
        error.response.data.ValidationErrors
      ) {
        const { ValidationErrors } = error.response.data;

        const firstNameError = findErrorField(ValidationErrors, 'FirstName');
        const lastNameError = findErrorField(ValidationErrors, 'LastName');
        const emailError = findErrorField(ValidationErrors, 'Email');
        const officePhoneError = findErrorField(
          ValidationErrors,
          'OfficePhone',
        );
        const passwordError = findErrorField(ValidationErrors, 'Password');
        const passwordConfirmError = findErrorField(
          ValidationErrors,
          'PasswordConfirm',
        );

        setErrors({
          firstName: firstNameError && firstNameError.Messages[0],
          lastName: lastNameError && lastNameError.Messages[0],
          email: emailError && emailError.Messages[0],
          officePhone: officePhoneError && officePhoneError.Messages[0],
          password: passwordError && passwordError.Messages[0],
          passwordConfirm:
            passwordConfirmError && passwordConfirmError.Messages[0],
        });
      } else {
        setServerError('Something went wrong. Please try again.');
        toast.error('Something went wrong. Please try again.', {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    };

    return axios
      .request(requestConfig)
      .then(handleSucces)
      .catch(handleError);
  };

  const editUserSumbmit = user => {
    const requestConfigEditUser = {
      url: `${process.env.REACT_APP_BASE_URL}manage-users/agencies/${user.agencyId}/users/${userId}`,
      method: 'put',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      data: JSON.stringify({ ...user, prevEmail, isEditMode: true }),
    };

    requestCallBack(requestConfigEditUser);
  };

  const newUserSumbmit = user => {
    const requestConfigAddUser = {
      url: `${process.env.REACT_APP_BASE_URL}manage-users/agencies/${user.agencyId}`,
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      data: JSON.stringify(user),
    };

    requestCallBack(requestConfigAddUser);
  };

  const onSubmit = e => {
    e.preventDefault();
    let passwordValidation;
    let newOrChangePassUser;
    let addEditUser;

    const user = {
      firstName: firstName.value,
      lastName: lastName.value,
      email: email.value,
      officePhone: officePhone.value,
      officeExtension: officePhoneExtension.value,
      cellPhone: cellPhone.value,
      securityLevel: securityLevel.value.value,
      agencyId: agencyId.value.value,
    };

    const userValidation = UserValidator.validate(user);

    if (userValidation && userValidation.isValid) {
      if ((isEditUser && isChangePassword) || isNewUser) {
        newOrChangePassUser = {
          ...user,
          changePassword: isChangePassword,
          password: password.value,
          passwordConfirm: passwordConfirm.value,
        };

        passwordValidation = PasswordValidator.validate({
          password: password.value,
          passwordConfirm: passwordConfirm.value,
        });

        addEditUser = {
          ...user,
          ...newOrChangePassUser,
          isAccountLocked: isLocked,
          securityLevelId: user.securityLevel,
          officePhoneExtension: user.officeExtension,
        };

        if (passwordValidation && passwordValidation.isValid) {
          if (password.value !== passwordConfirm.value) {
            setErrors({
              passwordConfirm: "Confirm password doesn't match password",
            });
            return;
          }
          if (isNewUser) {
            newUserSumbmit(addEditUser);
            return;
          }
          if (isEditUser) {
            editUserSumbmit(addEditUser);
          }
        } else {
          setErrors({
            firstName: userValidation.firstName.message,
            lastName: userValidation.lastName.message,
            email: userValidation.email.message,
            officePhone: userValidation.officePhone.message,
            securityLevel: userValidation.securityLevel.message,
            agencyId: userValidation.agencyId.message,
            password: passwordValidation.password.message,
            passwordConfirm: passwordValidation.passwordConfirm.message,
          });
        }
      } else {
        addEditUser = {
          ...user,
          isAccountLocked: isLocked,
          securityLevelId: user.securityLevel,
          officePhoneExtension: user.officeExtension,
        };

        if (isNewUser) {
          newUserSumbmit(addEditUser);
          return;
        }
        if (isEditUser) {
          editUserSumbmit(addEditUser);
        }
      }
    } else {
      setErrors({
        firstName: userValidation.firstName.message,
        lastName: userValidation.lastName.message,
        email: userValidation.email.message,
        officePhone: userValidation.officePhone.message,
        securityLevel: userValidation.securityLevel.message,
        agencyId: userValidation.agencyId.message,
      });
    }
  };

  const handlePhone = value => {
    const normalizedPhone = Normalizer.normalizePhoneOrFax(value);
    return Format.formatPhoneOrFax(normalizedPhone);
  };

  const handleExtension = value => {
    const normolizedExtension = Normalizer.nomralizeExt(value);
    return Format.formatPhoneExtension(normolizedExtension);
  };

  return (
    <>
      <ToastContainer />
      <form className={style.form}>
        <Input
          id="firstName"
          type="text"
          name="firstName"
          label="First Name:"
          autocomplete="off"
          styleLabel={style.label}
          styles={style.input}
          error={errors.firstName}
          {...firstName}
        />
        <Input
          type="text"
          name="lastName"
          label="Last Name:"
          autocomplete="off"
          styleLabel={style.label}
          styles={style.input}
          error={errors.lastName}
          {...lastName}
        />
        <Input
          type="text"
          name="email"
          label="Email:"
          autocomplete="off"
          styleLabel={style.label}
          styles={style.input}
          error={errors.email}
          {...email}
        />
        <Input
          type="text"
          name="officePhone"
          label="Office Phone:"
          autocomplete="off"
          styleLabel={style.label}
          styles={style.input}
          error={errors.officePhone}
          value={handlePhone(officePhone.value)}
          onChange={officePhone.onChange}
        />
        <Input
          type="text"
          name="officeExtension"
          label="Office Extension:"
          autocomplete="off"
          styleLabel={style.label}
          styles={style.input}
          value={handleExtension(officePhoneExtension.value)}
          onChange={officePhoneExtension.onChange}
        />
        <Input
          type="text"
          name="cellPhone"
          label="Cell Phone:"
          autocomplete="off"
          styleLabel={style.label}
          styles={style.input}
          value={handlePhone(cellPhone.value)}
          onChange={cellPhone.onChange}
        />
        <Select2
          label="HUB:"
          placeholder="Select agency"
          styles={{
            margin: '10px 0px 20px 0px',
            color: '#636176',
            width: '430px',
            height: '2.75rem',
            borderColor: '#b0aeca',
          }}
          {...agencyId}
          options={hubs || []}
          initialValue={agencyId.value && agencyId.value.value}
          error={errors.agencyId}
          disabled={isEditUser}
        />
        <Select2
          label="Security Level:"
          placeholder="Select security level"
          styles={{
            margin: '10px 0px 20px 0px',
            color: '#636176',
            width: '430px',
            height: '2.75rem',
            borderColor: '#b0aeca',
          }}
          {...securityLevel}
          options={securityLevels || []}
          initialValue={securityLevel.value && securityLevel.value.value}
          error={errors.securityLevel}
        />
        <div>
          <CheckBox
            id="isLocked"
            name="isLocked"
            onChange={() => setIsLocked(!isLocked)}
            checked={isLocked}
          />
          <label className={style.checkboxLabel} htmlFor="isLocked">
            Account is locked
          </label>
        </div>
        {!isEditUser ? (
          <PasswordGroup
            password={password}
            passwordConfirm={passwordConfirm}
            errorPassword={errors.password}
            errorPasswordConfirm={errors.passwordConfirm}
            style={style}
          />
        ) : null}
        {!isEditUser ? null : (
          <>
            <CheckBox
              id="сhangePassword"
              name="сhangePassword"
              label="Click here to change password"
              onChange={() => setIsChangePassword(!isChangePassword)}
              checked={isChangePassword}
            />
            <label className={style.checkboxLabel} htmlFor="сhangePassword">
              Click here to change password
            </label>
            {isChangePassword ? (
              <PasswordGroup
                password={password}
                passwordConfirm={passwordConfirm}
                errorPassword={errors.password}
                errorPasswordConfirm={errors.passwordConfirm}
                style={style}
              />
            ) : null}
          </>
        )}
      </form>
      <div className={style.btnGroup}>
        <button
          onClick={() => {
            if (isNewUser) {
              history.push({
                pathname: '/users',
                state: { ...userState, isNewUser: false },
              });
            }
            if (isEditUser) {
              history.push({
                pathname: '/users',
                state: { ...userState, isEditUser: false, isEditMode: false },
              });
            }
          }}
          type="button"
          className={`${style.btn} ${style.btnDefault}`}
        >
          Cancel
        </button>
        <PrimaryButton
          text="Save"
          styles={{
            width: '160px',
            marginTop: '0px',
          }}
          onClick={e => onSubmit(e)}
        />
      </div>
    </>
  );
};

UserForm.propTypes = {
  style: PropTypes.shape({}).isRequired,
  userState: PropTypes.shape({
    isEditUser: PropTypes.bool,
    isNewUser: PropTypes.bool,
    user: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
    }),
    securityLevels: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        value: PropTypes.string,
        text: PropTypes.string,
      }),
    ).isRequired,
    hubs: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        value: PropTypes.string,
        text: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
};

export default UserForm;
